import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAnalytics } from '../analytics/use-analytics';
import { useHost } from '../utils/use-host';

import { buildAuthUrl } from './auth-url-builder';
import { decodeState, encodeState, getClientId } from './auth-utils';
import { AuthenticationResponse, OrganisationAuthSettings, PostLogin } from './types';
import { useOrganisation } from './use-organisation';

const UNAUTHORISED_STATUS = 401;

type UseInitiateChangePassword = {
  cognitoDomain: string;
  organisation?: string;
  portalHost: string;
  clientId?: string;
  getOrgSettings: (org: string) => Promise<OrganisationAuthSettings>;
  setClientId: (s: string) => void;
};

const useInitiateChangePassword = ({
  portalHost,
  cognitoDomain,
  getOrgSettings,
  organisation,
  setClientId,
  clientId,
}: UseInitiateChangePassword) =>
  useCallback(async () => {
    if (organisation && !clientId) {
      const settings = await getOrgSettings(organisation);
      const redirectUri = encodeURIComponent(`${portalHost}/auth/callback`);
      const authClientId = settings?.client_id;

      if (!authClientId) throw new Error('No auth client ID found');

      if (settings?.client_id) {
        setClientId(settings.client_id);
        const changePasswordUrl = buildAuthUrl({
          cognitoDomain,
          clientId: settings.client_id,
          redirectUri,
          type: 'forgotPassword',
        });

        window.location.href = changePasswordUrl;
      }
    } else if (!organisation && clientId) {
      const redirectUri = encodeURIComponent(`${portalHost}/auth/callback`);
      const changePasswordUrl = buildAuthUrl({
        cognitoDomain,
        clientId,
        redirectUri,
        type: 'forgotPassword',
      });

      window.location.href = changePasswordUrl;
    }
  }, [clientId, cognitoDomain, getOrgSettings, organisation, portalHost, setClientId]);

type UseInitiateHostedLogin = {
  cognitoDomain: string;
  organisation?: string;
  portalHost: string;
  clientId?: string;
  getOrgSettings: (org: string) => Promise<OrganisationAuthSettings | undefined>;
  setClientId: (s: string) => void;
};

const useInitiateHostedLogin = ({
  portalHost,
  cognitoDomain,
  getOrgSettings,
  organisation,
  setClientId,
  clientId,
}: UseInitiateHostedLogin) =>
  useCallback(
    async (postLoginRedirect?: string) => {
      const redirectUri = encodeURIComponent(`${portalHost}/auth/callback`);
      const state = encodeState({
        organisation,
        postLoginRedirect,
      });
      if (organisation && !clientId) {
        const settings = await getOrgSettings(organisation);
        const authClientId = getClientId(settings);

        setClientId(authClientId);
        const authorizationUrl = buildAuthUrl({
          clientId: authClientId,
          cognitoDomain,
          redirectUri,
          state,
          type: 'login',
        });

        window.location.href = authorizationUrl;
      } else if (!organisation && clientId) {
        const authorizationUrl = buildAuthUrl({
          clientId,
          cognitoDomain,
          redirectUri,
          state,
          type: 'login',
        });

        window.location.href = authorizationUrl;
      }
    },
    [clientId, cognitoDomain, getOrgSettings, organisation, portalHost, setClientId]
  );

type UseHandleCallback = {
  apiUrl: string;
  setAuthError: (s: string) => void;
  setClientId: (s: string) => void;
  postLogin: PostLogin;
};

const useHandleCallback = ({ apiUrl, setAuthError, setClientId, postLogin }: UseHandleCallback) => {
  const location = useLocation();
  const portalHost = useHost();

  const handleCallback = async () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      const encodedState = searchParams.get('state');
      const { organisation, postLoginRedirect } = decodeState(encodedState);

      if (!code) {
        throw new Error('No authorization code found');
      }

      const response = await fetch(`${apiUrl}/auth/callback`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          redirect: `${portalHost}/auth/callback`,
          organisation,
        }),
      });

      if (response.status === UNAUTHORISED_STATUS) {
        const responsePayload = await response.json();
        const errorMessage = responsePayload.message ?? 'Not authorised for this organisation';
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error('Failed to exchange authorisation code');
      }

      const responsePayload = (await response.json()) as AuthenticationResponse;

      setClientId(responsePayload.clientId);
      postLogin(responsePayload, postLoginRedirect);
    } catch (err) {
      if (err && typeof err === 'object' && 'message' in err) setAuthError(`${err.message}`);
    }
  };

  return handleCallback;
};

type UsePostLogin = {
  setIsAuthenticated: (b: boolean) => void;
  setAuthenticatedUser: (s: AuthenticationResponse) => void;
  setClientId: (s: string) => void;
  setAuthSystemId: (s: string) => void;
  setIsDefaultClientId: (b: boolean) => void;
};

const usePostLogin = ({
  setIsAuthenticated,
  setAuthenticatedUser,
  setClientId,
  setIsDefaultClientId,
  setAuthSystemId,
}: UsePostLogin) => {
  const [organisation, setOrganisation] = useOrganisation();
  const analytics = useAnalytics();

  const navigate = useNavigate();
  return useCallback(
    (response: AuthenticationResponse, redirect?: string) => {
      setIsAuthenticated(true);
      setClientId(response.clientId);
      setIsDefaultClientId(response.isDefaultClientId);
      setAuthenticatedUser(response);
      setOrganisation(response.organisation);
      setAuthSystemId(response.authSystemId);

      if (response.authSystemId) {
        analytics.identify({
          distinctId: response.authSystemId,
          properties: {
            last_login: new Date().toISOString(),
            organisation: response.organisation,
          },
        });
      }

      if (response.organisation) {
        analytics.setGroup({
          groupType: 'organisation',
          groupKey: response.organisation,
          properties: {
            name: response.organisation,
          },
        });
      }

      if (redirect) navigate(redirect);
    },
    [organisation, setIsAuthenticated, setAuthenticatedUser, setOrganisation, navigate, analytics]
  );
};

const SESSION_STORAGE_KEYS_TO_CLEAR = ['invitePublicId'];

type Logout = (path?: string, dataToStore?: Record<string, string>) => Promise<Response | undefined>;
const createLogout = ({
  portalHost,
  apiUrl,
  cognitoDomain,
  setIsAuthenticated,
  clientId,
  analytics,
}: {
  portalHost: string;
  apiUrl: string;
  cognitoDomain: string;
  setIsAuthenticated: (b: boolean) => void;
  clientId?: string;
  analytics: ReturnType<typeof useAnalytics>;
}) => {
  const logout: Logout = async (path, dataToStore = {}) => {
    const logoutUri = encodeURIComponent(`${portalHost}/${path || ''}`);

    SESSION_STORAGE_KEYS_TO_CLEAR.forEach((key) => localStorage.removeItem(key));

    Object.entries(dataToStore).forEach(([key, value]) => sessionStorage.setItem(key, value));
    return fetch(`${apiUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
      .catch(() => undefined)
      .finally(() => {
        analytics.trackEvent({
          event: 'logout',
        });
        analytics.reset();
        setIsAuthenticated(false);
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
      });
  };
  return logout;
};

const useChangePassword = (apiUrl: string) =>
  useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      const res = await fetch(`${apiUrl}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const errorMessage = data?.message || 'Password change failed';
        throw new Error(errorMessage);
      }
    },
    [apiUrl]
  );

export {
  createLogout,
  useChangePassword,
  useHandleCallback,
  useInitiateChangePassword,
  useInitiateHostedLogin,
  usePostLogin,
};

export type { Logout, UseHandleCallback, UseInitiateChangePassword, UseInitiateHostedLogin, UsePostLogin };
